/**
 * useRole Hook
 *
 * React Query hook for fetching a single role with full details.
 * Includes assigned users and groups information.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';
import type { RoleSanitized } from '../../../types/role.types';

/**
 * Fetch a single role by ID
 *
 * @param tenantId - Tenant ID
 * @param roleId - Role ID to fetch
 * @returns Query result with role details
 */
export const useRole = (
  tenantId: string,
  roleId: string | null
): UseQueryResult<RoleSanitized, Error> => {
  return useQuery<RoleSanitized, Error>({
    queryKey: ['role', tenantId, roleId],
    queryFn: async () => {
      if (!roleId) throw new Error('Role ID is required');
      return await roleService.getRole(tenantId, roleId);
    },
    enabled: !!tenantId && !!roleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
